<template>
  <div class="bg-white dark-bg-custom">
    <v-row class="flex justify-between p-4" no-gutters>
      <h4 class="mb-0 text-xl font-bold">Salary Slip</h4>
      <div class="flex justify-end gap-2">
        <v-btn
          color="primary"
          small
          outlined
          @click="downloadAsPDF"
          :loading="downloading"
          class="normal-case"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
        <v-icon @click="handleClose">mdi-close</v-icon>
      </div>
    </v-row>
    <v-divider></v-divider>

    <!-- Year Selection Control -->
    <div class="px-3">
      <v-row class="my-2 align-center">
        <v-col cols="12" class="d-flex justify-center align-center p-0">
          <v-btn icon @click="previousYear" :disabled="selectedYear <= minYear">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <div class="text-h6 mx-4">{{ selectedYear }}</div>
          <v-btn icon @click="nextYear" :disabled="selectedYear >= maxYear">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Tax Regime Display -->
    <div class="px-3" v-if="employeeTaxData">
      <v-row>
        <v-col cols="12">
          <v-card outlined class="py-0">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon left color="primary">mdi-file-certificate</v-icon>
                <span class="text-h7">Tax Regime: </span>
                <v-chip
                  :color="employeeTaxData.taxRegime === 'new' ? 'success' : 'warning'"
                  small
                  class="ml-2"
                >
                  {{ employeeTaxData.taxRegime === 'new' ? 'New Regime' : 'Old Regime' }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div class="px-3" v-else>
      <v-row class="">
        <v-col cols="12">
          <v-card outlined>
            <v-card-text class="py-0">
              <div class="d-flex align-center">
                <v-icon left color="primary">mdi-file-certificate</v-icon>
                <span class="text-h7">Tax Regime: </span>
                <v-chip color="grey" small class="ml-2"> Not Selected </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Month Selection -->
    <div class="px-8 mt-4">
      <v-row>
        <v-col cols="12">
          <v-row>
            <v-col
              v-for="(month, index) in availableMonths"
              :key="index"
              cols="3"
              sm="2"
              md="1"
              class="pa-1"
            >
              <v-btn
                :color="selectedMonth === index ? $vuetify.theme.currentTheme.primary : 'default'"
                :class="selectedMonth === index ? 'white--text' : ''"
                @click="selectMonth(index)"
                block
                small
                :disabled="!isMonthEnabled(index)"
              >
                {{ month }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>

    <!-- Single PDF component for both UI display and PDF generation -->
    <SalarySlipPDFView
      ref="pdfComponent"
      :employee-data="employeeData"
      :org-data="orgData"
      :salary-data="salaryData"
      :selected-year="selectedYear"
      :selected-month="selectedMonth"
      :standard-days="standardDays"
      :work-days="workDays"
      :lop-days="lopDays"
      :employerSignature="employerSignature"
      :employee-tax-data="employeeTaxData"
    />
    <!-- </div> -->
  </div>
</template>

<script>
import { fullName } from '@/utils/common'
import SalarySlipPDFView from './SalarySlipPDFView.vue'
import html2pdf from 'html2pdf.js'

export default {
  name: 'EmployeeSalarySlip',
  components: {
    SalarySlipPDFView
  },
  props: {
    employeeData: Object,
    employeeImage: String,
    branch: Object
  },
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      minYear: new Date().getFullYear() - 5, // 5 years back
      maxYear: new Date().getFullYear(), // Current year (prevent going to future years)
      selectedMonth: null, // 0-11 (January-December)
      salaryData: null,
      salaryTemplate: null, // Store the fetched salary template
      loading: false,
      standardDays: 0,
      workDays: 0,
      lopDays: 0,
      downloading: false,
      employeeTaxData: null, // Added to store employee tax regime data
      salaryLogs: []
    }
  },
  computed: {
    employerSignature() {
      const branch = this.$props.branch || {}
      return `${process.env.VUE_APP_S3_BASE_URL}${branch?.authorizedSignature}` || ''
    },
    orgData() {
      const orgData = this.$storage.getUniversal('orgData')
      return orgData
    },
    availableMonths() {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ]

      // For current year, only show months up to current month
      if (this.selectedYear === new Date().getFullYear()) {
        const currentMonth = new Date().getMonth() // 0-11
        return months.slice(0, currentMonth)
      }

      // For past years, show all months
      if (this.selectedYear < new Date().getFullYear()) {
        return months
      }

      // For future years, don't show any months
      return []
    }
  },
  watch: {
    employeeData: {
      handler(newEmployeeData, oldEmployeeData) {
        if (newEmployeeData && newEmployeeData !== oldEmployeeData) {
          // Reset the salary data
          this.salaryData = null
          this.salaryTemplate = null
          this.employeeTaxData = null // Reset tax data
          this.fetchSalaryLogs()

          if (this.selectedMonth !== null) {
            this.fetchSalaryData()
            this.fetchEmployeeTaxData() // Fetch tax data when employee data changes
          }
        }
      },
      deep: true
    },
    selectedYear() {
      this.selectedMonth = null
      this.salaryData = null
      this.employeeTaxData = null // Reset tax data when year changes

      this.$nextTick(() => {
        // Try to select the first enabled month
        const enabledMonthIndex = this.availableMonths.findIndex((_, index) =>
          this.isMonthEnabled(index)
        )
        if (enabledMonthIndex !== -1) {
          this.selectMonth(enabledMonthIndex)
        }
      })
    },
    selectedMonth() {
      if (this.selectedMonth !== null) {
        this.fetchSalaryData()
        this.fetchEmployeeTaxData() // Fetch tax data when month changes
      } else {
        this.salaryData = null
        this.employeeTaxData = null
      }
    }
  },
  methods: {
    employeeFullName(item) {
      if (item) {
        return fullName(this.employeeData)
      }
      return ''
    },
    previousYear() {
      if (this.selectedYear > this.minYear) {
        this.selectedYear--
      }
    },
    nextYear() {
      // Prevent going to future years beyond current year
      if (this.selectedYear < this.maxYear) {
        this.selectedYear++
      }
    },
    selectMonth(monthIndex) {
      this.selectedMonth = monthIndex
    },
    async fetchSalaryLogs() {
      if (!this.employeeData || !this.employeeData._id) return
      try {
        const response = await this.$axios.get(
          `/orgSetup/salary-logs?employee=${this.employeeData._id}`
        )
        this.salaryLogs = response.data.salaryLogs || []
      } catch (error) {
        console.error('Error fetching salary logs:', error)
        this.salaryLogs = []
      }
    },
    isMonthEnabled(monthIndex) {
      if (!this.salaryLogs.length) return false
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ]
      const monthName = monthNames[monthIndex]
      return this.salaryLogs.some(
        (log) => log.salaryMonth === monthName && log.salaryYear === this.selectedYear
      )
    },
    async fetchSalaryData() {
      this.loading = true
      try {
        const designationId = this.employeeData.designation?._id
        const departmentId = this.employeeData.department?._id
        const branchId = this.branch?._id || this.employeeData.branch?._id
        const year = this.selectedYear

        let salaryTemplates = []

        // Helper function to fetch templates with specific params
        const fetchTemplates = async (params) => {
          try {
            const response = await this.$axios.get('/org/salary-templates', { params })
            return response.data.salaryTemplates || []
          } catch (error) {
            console.error('Error fetching salary templates with params:', params, error)
            return []
          }
        }

        // 1. Try with all 3: branch + designation + department + year
        if (branchId && designationId && departmentId) {
          salaryTemplates = await fetchTemplates({
            branch: branchId,
            designation: designationId,
            department: departmentId,
            year: year
          })
        }

        // 2. If not found, try with branch + year (designation=null, department=null)
        if ((!salaryTemplates || salaryTemplates.length === 0) && branchId) {
          salaryTemplates = await fetchTemplates({
            branch: branchId,
            designation: 'null',
            department: 'null',
            year: year
          })
        }

        // 3. If still not found, try generic: year only (others null)
        if (!salaryTemplates || salaryTemplates.length === 0) {
          salaryTemplates = await fetchTemplates({
            branch: 'null',
            designation: 'null',
            department: 'null',
            year: year
          })
        }

        if (!salaryTemplates || salaryTemplates.length === 0) {
          throw new Error('No salary template found for this role')
        }

        // Use the first template found
        this.salaryTemplate = salaryTemplates[0]

        // Calculate the salary breakdown based on employee's CTC
        this.calculateSalaryBreakdown()
      } catch (error) {
        console.error('Error fetching salary data:', error)
        this.$toast.error(error.message || 'Failed to fetch salary data')
        this.salaryData = null
      } finally {
        this.loading = false
      }
    },
    calculateSalaryBreakdown() {
      if (!this.salaryTemplate || !this.employeeData.ctc) {
        this.salaryData = null
        return
      }

      const ctc = parseFloat(this.employeeData.ctc) / 12 || 0
      const professionalTax = parseFloat(this.salaryTemplate.professionalTax) / 12 || 0

      // Calculate Income Tax (TDS) - New Regime Logic (FY 2025-26)
      let incomeTax = 0
      const annualCTC = parseFloat(this.employeeData.ctc) || 0
      if (annualCTC > 0) {
        const annualProfessionalTax = parseFloat(this.salaryTemplate.professionalTax) || 0
        const standardDeduction = parseFloat(this.salaryTemplate.standardDeduction) || 50000 // Standard Deduction
        const taxableIncome = Math.max(0, annualCTC - standardDeduction - annualProfessionalTax)
        const annualTax = this.calculateIncomeTaxNewRegime(taxableIncome)
        incomeTax = annualTax / 12
      }

      if (ctc <= 0) {
        this.salaryData = null
        return
      }

      // Get percentages from template
      const basicSalaryPercent = parseFloat(this.salaryTemplate.basicSalaryPercent) || 0
      const hraPercent = parseFloat(this.salaryTemplate.hraPercent) || 0
      const medicalAllowance = parseFloat(this.salaryTemplate.medicalAllowance) / 12 || 0
      const conveyanceAllowance = parseFloat(this.salaryTemplate.conveyanceAllowance) / 12 || 0
      const ltaAllowance = parseFloat(this.salaryTemplate.ltaPercent) / 12 || 0

      // Calculate basic salary (percentage of CTC)
      const basicSalary = (ctc * basicSalaryPercent) / 100

      // Calculate HRA (percentage of Basic)
      const hraAmount = (basicSalary * hraPercent) / 100

      // Calculate special allowance (what's left after deducting all other components from CTC)
      // Subtract tax saving investments from special allowance
      const taxSavingAmount = this.salaryTemplate.taxSavingComponents
        ? this.salaryTemplate.taxSavingComponents.reduce(
            (sum, investment) => sum + (parseFloat(investment.amount) || 0),
            0
          ) / 12
        : 0

      const specialAllowance =
        ctc -
        (basicSalary +
          hraAmount +
          medicalAllowance +
          ltaAllowance +
          conveyanceAllowance +
          taxSavingAmount)

      // Prepare earnings
      const basicEarnings = [
        { name: 'Basic Salary', amount: parseFloat(basicSalary.toFixed(2)) },
        { name: 'House Rent Allowance', amount: parseFloat(hraAmount.toFixed(2)) },
        { name: 'Medical Allowance', amount: parseFloat(medicalAllowance.toFixed(2)) },
        { name: 'Leave Travel Allowance', amount: parseFloat(ltaAllowance.toFixed(2)) },
        { name: 'Conveyance Allowance', amount: parseFloat(conveyanceAllowance.toFixed(2)) }
      ]

      // Calculate employee PF (12% of basic salary)
      const employeePF = (basicSalary * 12) / 100

      // Prepare deductions
      const deductions = [
        { name: 'Employee Provident Fund', amount: parseFloat(employeePF.toFixed(2)) },
        { name: 'Professional Tax', amount: parseFloat(professionalTax.toFixed(2)) },
        { name: 'Income Tax (TDS)', amount: parseFloat(incomeTax.toFixed(2)) }
      ]

      // Add additional deductions from template
      let additionalDeductions = []
      if (
        this.salaryTemplate.additionalDeductions &&
        Array.isArray(this.salaryTemplate.additionalDeductions)
      ) {
        additionalDeductions = this.salaryTemplate.additionalDeductions.map((deduction) => ({
          name: deduction.deductionType,
          amount: parseFloat((parseFloat(deduction.amount) / 12).toFixed(2)) || 0
        }))
        deductions.push(...additionalDeductions)
      }

      // Prepare tax saving investments from template
      let taxSavingInvestments = []
      if (
        this.salaryTemplate.taxSavingComponents &&
        Array.isArray(this.salaryTemplate.taxSavingComponents)
      ) {
        taxSavingInvestments = this.salaryTemplate.taxSavingComponents.map((investment) => ({
          name: investment.componentName,
          amount: parseFloat((parseFloat(investment.amount) / 12).toFixed(2)) || 0
        }))
      }
      const spAllowance = [
        { name: 'Special Allowance', amount: Math.max(0, parseFloat(specialAllowance.toFixed(2))) }
      ]
      const earnings = [...basicEarnings, ...taxSavingInvestments, ...spAllowance]
      // Set the calculated salary data
      this.salaryData = {
        earnings,
        deductions
      }
    },

    convertImageToBase64: async function (imageUrl) {
      try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()

        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        console.error('Error converting image:', error)
        return null
      }
    },

    processImages: async function (element) {
      const images = element.querySelectorAll('.org-info-section')
      console.log({ images })
      for (let img of images) {
        const base64 = await this.convertImageToBase64(img.src)
        console.log({ base64 })
        if (base64) {
          img.src = base64
        }
      }
    },

    downloadAsPDF: async function () {
      this.downloading = true

      try {
        await this.$nextTick()

        const pdfElement = this.$refs.pdfComponent.$el.cloneNode(true)

        const signatureSection = pdfElement.querySelector('#signature-section')
        if (signatureSection) {
          signatureSection.style.display = 'block'
          signatureSection.style.opacity = '1'
        }
        console.log({ pdfElement })

        // await this.processImages(pdfElement)

        // Configure html2pdf options for A4
        const options = {
          margin: [10, 5, 10, 5],
          filename: `Salary_Slip_${this.employeeFullName(this.employeeData)}_${
            this.availableMonths[this.selectedMonth]
          }_${this.selectedYear}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        }

        // Generate and download PDF
        html2pdf()
          .set(options)
          .from(pdfElement)
          .save()
          .then(() => {
            this.downloading = false
            this.$toast.success('Salary slip downloaded successfully')
          })
          .catch((error) => {
            this.downloading = false
            console.error('Error generating PDF:', error)
            this.$toast.error('Failed to download salary slip')
          })
      } catch (error) {
        this.downloading = false
        console.error('Error processing PDF:', error)
        this.$toast.error('Failed to process salary slip for download')
      }
    },

    handleClose() {
      this.$emit('handleSalarySlipClose')
    },

    calculateIncomeTaxNewRegime(taxableIncome) {
      // New Tax Regime Slabs (FY 2025-26 onwards)
      // Standard Deduction of 75k is expected to be applied before calling this

      if (taxableIncome <= 400000) return 0

      let tax = 0

      // 4L - 8L: 5%
      if (taxableIncome > 400000) {
        tax += Math.min(taxableIncome - 400000, 400000) * 0.05
      }

      // 8L - 12L: 10%
      if (taxableIncome > 800000) {
        tax += Math.min(taxableIncome - 800000, 400000) * 0.1
      }

      // 12L - 16L: 15%
      if (taxableIncome > 1200000) {
        tax += Math.min(taxableIncome - 1200000, 400000) * 0.15
      }

      // 16L - 20L: 20%
      if (taxableIncome > 1600000) {
        tax += Math.min(taxableIncome - 1600000, 400000) * 0.2
      }

      // 20L - 24L: 25%
      if (taxableIncome > 2000000) {
        tax += Math.min(taxableIncome - 2000000, 400000) * 0.25
      }

      // >24L: 30%
      if (taxableIncome > 2400000) {
        tax += (taxableIncome - 2400000) * 0.3
      }

      // Rebate u/s 87A: Tax is nil if Taxable Income <= 12 Lakhs
      if (taxableIncome <= 1200000) {
        tax = 0
      } else {
        // Marginal Relief logic: Tax payable should not exceed (Income - 12,00,000)
        // for cases just above 12L where tax spike happens.
        const excessIncome = taxableIncome - 1200000
        if (tax > excessIncome) {
          tax = excessIncome
        }
      }

      // Cess 4%
      return tax * 1.04
    },

    async fetchEmployeeTaxData() {
      if (!this.employeeData || !this.employeeData._id) {
        console.warn('Employee ID not available')
        return
      }

      try {
        const response = await this.$axios.get(
          `/employee/employee-tax/${this.employeeData._id}/${this.selectedYear}`
        )
        if (response.data.success) {
          this.employeeTaxData = response.data.employeeTax
        } else {
          console.warn('Failed to fetch employee tax data:', response.data.message)
          this.employeeTaxData = null
        }
      } catch (error) {
        console.error('Error fetching employee tax data:', error)
        this.employeeTaxData = null
      }
    }
  },
  mounted() {
    this.fetchSalaryLogs()
    // Initialize with current month
    const currentMonth = new Date().getMonth()
    if (this.availableMonths.length > currentMonth) {
      this.selectedMonth = currentMonth
    } else if (this.availableMonths.length > 0) {
      this.selectedMonth = this.availableMonths.length - 1
    }
  }
}
</script>

<style scoped>
.font-semibold {
  font-weight: 600;
}
</style>
